import { Employee } from '../types/hr';

const OFFLINE_AUTH_KEY = 'offline_auth_state';
const AUTH_CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

export interface OfflineAuthData {
  isAuthenticated: boolean;
  userId: string | null;
  userData: Employee | null;
  timestamp: number;
  email: string | null;
}

class OfflineAuthService {
  cacheAuthState(userId: string, userData: Employee, email: string): void {
    try {
      const authData: OfflineAuthData = {
        isAuthenticated: true,
        userId,
        userData,
        timestamp: Date.now(),
        email,
      };
      
      localStorage.setItem(OFFLINE_AUTH_KEY, JSON.stringify(authData));
      // console.log('[OFFLINE_AUTH] Authentication state cached for offline use');
    } catch (error) {
      console.error('[OFFLINE_AUTH] Failed to cache auth state:', error);
    }
  }

  /**
   * Get cached authentication state
   */
  getCachedAuthState(): OfflineAuthData | null {
    try {
      const cached = localStorage.getItem(OFFLINE_AUTH_KEY);
      if (!cached) return null;

      const authData: OfflineAuthData = JSON.parse(cached);
      
      // Check if cache is expired
      const age = Date.now() - authData.timestamp;
      if (age > AUTH_CACHE_DURATION) {
        console.log('[OFFLINE_AUTH] Cached auth state expired, clearing cache');
        this.clearAuthState();
        return null;
      }

      console.log('[OFFLINE_AUTH] Using cached auth state (offline mode)');
      return authData;
    } catch (error) {
      console.error('[OFFLINE_AUTH] Failed to get cached auth state:', error);
      return null;
    }
  }

  /**
   * Clear cached authentication state
   */
  clearAuthState(): void {
    try {
      localStorage.removeItem(OFFLINE_AUTH_KEY);
      console.log('[OFFLINE_AUTH] Cached auth state cleared');
    } catch (error) {
      console.error('[OFFLINE_AUTH] Failed to clear cached auth state:', error);
    }
  }

  /**
   * Check if we should use offline auth (when navigator is offline)
   */
  shouldUseOfflineAuth(): boolean {
    return !navigator.onLine;
  }

  /**
   * Update cached user data (for profile updates)
   */
  updateCachedUserData(userData: Employee): void {
    try {
      const cached = this.getCachedAuthState();
      if (cached) {
        cached.userData = userData;
        cached.timestamp = Date.now(); // Update timestamp
        localStorage.setItem(OFFLINE_AUTH_KEY, JSON.stringify(cached));
        console.log('[OFFLINE_AUTH] Cached user data updated');
      }
    } catch (error) {
      console.error('[OFFLINE_AUTH] Failed to update cached user data:', error);
    }
  }

  /**
   * Check if cached auth state exists and is valid
   */
  hasCachedAuth(): boolean {
    const cached = this.getCachedAuthState();
    return cached !== null && cached.isAuthenticated;
  }

  /**
   * Get cache expiry info for debugging
   */
  getCacheInfo(): { exists: boolean; age: number; expiresIn: number } | null {
    try {
      const cached = localStorage.getItem(OFFLINE_AUTH_KEY);
      if (!cached) return { exists: false, age: 0, expiresIn: 0 };

      const authData: OfflineAuthData = JSON.parse(cached);
      const age = Date.now() - authData.timestamp;
      const expiresIn = AUTH_CACHE_DURATION - age;

      return {
        exists: true,
        age,
        expiresIn: Math.max(0, expiresIn),
      };
    } catch (error) {
      console.error('[OFFLINE_AUTH] Failed to get cache info:', error);
      return null;
    }
  }
}

// Export singleton instance
export const offlineAuthService = new OfflineAuthService(); 